import { BlockRange, CurrencyType, SecurityIssuedIn } from '@dealstack/shared/lib/generated';
import { mapBlockToText } from '@dealstack/shared/lib/format/block.ts';
import { formatNumberToUI } from '@dealstack/shared/lib/format/number';
import {
	BlockEntitiesRowProps,
	EntityColorMapInput,
	LinkCalculatedProps,
} from './BlockEntities.types';

const DEFAULT_COLOR = '#bdc3c7';
const TRANSPARENT = 'transparent';

export const calculateLinkStates = ({
	fromEntityId,
	fromEntityName,
	toEntityId,
	toEntityName,
	issuerEntityId,
	mainEntityId,
	entityColorMap,
	fromBlocksHeld,
	toBlocksHeld,
	securityIssuedIn,
	currency,
	fromCost,
	toCost,
	securityUnitDenominator,
}: Omit<BlockEntitiesRowProps, 'date' | 'blocks' | 'units' | 'transactionCost'>): {
	link1: LinkCalculatedProps;
	link2: LinkCalculatedProps;
} => {
	const issuedInCurrency = securityIssuedIn === SecurityIssuedIn.CURRENCY;

	const sourceBlocks = formatBlocksOrCost(
		issuedInCurrency,
		fromCost,
		fromBlocksHeld,
		currency,
		securityUnitDenominator
	);
	const destinationBlocks = formatBlocksOrCost(
		issuedInCurrency,
		toCost,
		toBlocksHeld,
		currency,
		securityUnitDenominator
	);

	const link1 = createDefaultLink();
	const link2 = createDefaultLink();

	// Source entity relationships
	const comingFromIssuer = isEntity(fromEntityId, issuerEntityId);
	const comingFromMain = isEntity(fromEntityId, mainEntityId);
	const comingFromOther = isOtherEntity(fromEntityId, mainEntityId, issuerEntityId);

	// Destination entity relationships
	const goingToIssuer = isEntity(toEntityId, issuerEntityId);
	const goingToMain = isEntity(toEntityId, mainEntityId);
	const goingToOther = isOtherEntity(toEntityId, mainEntityId, issuerEntityId);

	const sourceSameAsDestination = fromEntityId === toEntityId;

	if (sourceSameAsDestination || !fromEntityId || !toEntityId) {
		const blocks = destinationBlocks.length ? destinationBlocks : sourceBlocks;
		const entityName = toEntityName ?? fromEntityName;
		const isIssuerEntity = comingFromIssuer ?? goingToIssuer;
		const isMainEntity = comingFromMain ?? goingToMain;

		configureSingleNode({
			link1,
			link2,
			entityColorMap,
			blocks,
			isIssuerEntity,
			isMainEntity,
			entityName,
		});

		return { link1, link2 };
	}

	if (comingFromOther && goingToOther) {
		return {
			link1: {
				...link1,
				lineColor: TRANSPARENT,
			},
			link2: {
				...link2,
				lineColor: TRANSPARENT,
			},
		};
	}

	// Set base colors and properties
	link1.sourceColor = entityColorMap.issuerEntityColor;
	link1.destinationColor = entityColorMap.mainEntityColor;
	link1.hasNodeRight = true;
	link2.destinationColor = entityColorMap.otherEntityColor;

	if (comingFromIssuer) {
		// Issuer -> Main
		if (goingToMain) {
			configureIssuerToMain(
				link1,
				entityColorMap,
				sourceBlocks,
				destinationBlocks,
				fromEntityName,
				toEntityName
			);
			link2.hidden = true;
		}

		// Issuer -> Other
		if (goingToOther) {
			configureIssuerToOther(
				link1,
				link2,
				entityColorMap,
				sourceBlocks,
				destinationBlocks,
				fromEntityName,
				toEntityName
			);
		}
	}

	if (comingFromMain) {
		// Issuer <- Main
		if (goingToIssuer) {
			configureMainToIssuer(
				link1,
				entityColorMap,
				sourceBlocks,
				destinationBlocks,
				fromEntityName,
				toEntityName
			);
			link2.hidden = true;
		}

		// Main -> Other
		if (goingToOther) {
			configureMainToOther(
				link1,
				link2,
				entityColorMap,
				sourceBlocks,
				destinationBlocks,
				fromEntityName,
				toEntityName
			);
		}
	}

	if (comingFromOther) {
		// Main <- Other
		if (goingToMain) {
			configureOtherToMain(
				link1,
				link2,
				entityColorMap,
				sourceBlocks,
				destinationBlocks,
				fromEntityName,
				toEntityName
			);
		}

		// Issuer <- Other
		if (goingToIssuer) {
			configureOtherToIssuer(
				link1,
				link2,
				entityColorMap,
				sourceBlocks,
				destinationBlocks,
				fromEntityName,
				toEntityName
			);
		}
	}

	return { link1, link2 };
};

const createDefaultLink = (): LinkCalculatedProps => ({
	hidden: false,
	hasNodeLeft: false,
	hasArrowLeft: false,
	hasNodeRight: false,
	hasArrowRight: false,
	showBox: false,
	sourceColor: DEFAULT_COLOR,
	destinationColor: DEFAULT_COLOR,
	lineColor: DEFAULT_COLOR,
	blockColor: DEFAULT_COLOR,
	nodeLeftName: '',
	nodeRightName: '',
	nodeLeftContextMenuItems: [],
	nodeRightContextMenuItems: [],
});

const formatBlocksOrCost = (
	issuedInCurrency: boolean,
	cost: number | undefined,
	blocks: BlockRange[],
	currency?: CurrencyType,
	unitDenominator?: number
): string[] => {
	if (issuedInCurrency) {
		return cost ? [formatNumberToUI(cost, currency, unitDenominator)] : [];
	}
	return blocks?.map((b) => mapBlockToText(b, unitDenominator)) ?? [];
};

const isEntity = (entityId: string | null | undefined, targetId: string): boolean =>
	entityId === targetId;

const isOtherEntity = (
	entityId: string | null | undefined,
	mainEntityId: string,
	issuerEntityId: string
): boolean => entityId !== null && entityId !== mainEntityId && entityId !== issuerEntityId;

const configureIssuerToMain = (
	link: LinkCalculatedProps,
	entityColorMap: EntityColorMapInput,
	sourceBlocks: string[],
	destinationBlocks: string[],
	fromEntityName: string | null,
	toEntityName: string | null
): void => {
	link.hasNodeLeft = true;
	link.showBox = true;
	link.hasArrowRight = true;
	link.lineColor = entityColorMap.issuerEntityColor;
	link.blockColor = entityColorMap.issuerEntityColor;
	link.nodeLeftContextMenuItems = sourceBlocks;
	link.nodeRightContextMenuItems = destinationBlocks;
	link.nodeLeftName = fromEntityName ?? '';
	link.nodeRightName = toEntityName ?? '';
};

const configureMainToIssuer = (
	link: LinkCalculatedProps,
	entityColorMap: EntityColorMapInput,
	sourceBlocks: string[],
	destinationBlocks: string[],
	fromEntityName: string | null,
	toEntityName: string | null
): void => {
	link.hasNodeLeft = true;
	link.showBox = true;
	link.hasArrowLeft = true;
	link.lineColor = entityColorMap.mainEntityColor;
	link.blockColor = entityColorMap.mainEntityColor;
	link.nodeLeftContextMenuItems = destinationBlocks;
	link.nodeRightContextMenuItems = sourceBlocks;
	link.nodeLeftName = toEntityName ?? '';
	link.nodeRightName = fromEntityName ?? '';
};

const configureMainToOther = (
	link1: LinkCalculatedProps,
	link2: LinkCalculatedProps,
	entityColorMap: EntityColorMapInput,
	sourceBlocks: string[],
	destinationBlocks: string[],
	fromEntityName: string | null,
	toEntityName: string | null
): void => {
	link1.hidden = true;
	link1.hasNodeLeft = false;
	link1.hasNodeRight = false;

	link2.hasNodeLeft = true;
	link2.hasNodeRight = true;
	link2.hasArrowRight = true;
	link2.showBox = true;
	link2.sourceColor = entityColorMap.mainEntityColor;
	link2.lineColor = entityColorMap.mainEntityColor;
	link2.blockColor = entityColorMap.mainEntityColor;
	link2.label = toEntityName ?? '';
	link2.nodeLeftContextMenuItems = sourceBlocks;
	link2.nodeLeftName = fromEntityName ?? '';
	link2.nodeRightContextMenuItems = destinationBlocks;
	link2.nodeRightName = toEntityName ?? '';
};

const configureOtherToIssuer = (
	link1: LinkCalculatedProps,
	link2: LinkCalculatedProps,
	entityColorMap: EntityColorMapInput,
	sourceBlocks: string[],
	destinationBlocks: string[],
	fromEntityName: string | null,
	toEntityName: string | null
): void => {
	link1.hasNodeLeft = true;
	link1.hasNodeRight = false;
	link1.hasArrowLeft = true;
	link1.lineColor = entityColorMap.otherEntityColor;
	link1.nodeLeftContextMenuItems = destinationBlocks;
	link1.nodeLeftName = toEntityName ?? '';

	link2.hasNodeLeft = false;
	link2.hasNodeRight = true;
	link2.hasArrowLeft = false;
	link2.showBox = true;
	link2.label = fromEntityName ?? '';
	link2.lineColor = entityColorMap.otherEntityColor;
	link2.nodeRightContextMenuItems = sourceBlocks;
	link2.nodeRightName = fromEntityName ?? '';
};

const configureOtherToMain = (
	link1: LinkCalculatedProps,
	link2: LinkCalculatedProps,
	entityColorMap: EntityColorMapInput,
	sourceBlocks: string[],
	destinationBlocks: string[],
	fromEntityName: string | null,
	toEntityName: string | null
): void => {
	link1.hasNodeRight = true;
	link1.lineColor = TRANSPARENT;
	link1.nodeRightContextMenuItems = destinationBlocks;
	link1.nodeRightName = toEntityName ?? '';

	link2.hidden = false;
	link2.hasNodeRight = true;
	link2.hasArrowLeft = true;
	link2.showBox = true;
	link2.lineColor = entityColorMap.otherEntityColor;
	link2.blockColor = entityColorMap.otherEntityColor;
	link2.nodeRightContextMenuItems = sourceBlocks;
	link2.nodeRightName = fromEntityName ?? '';
	link2.label = fromEntityName ?? '';
};

const configureIssuerToOther = (
	link1: LinkCalculatedProps,
	link2: LinkCalculatedProps,
	entityColorMap: EntityColorMapInput,
	sourceBlocks: string[],
	destinationBlocks: string[],
	fromEntityName: string | null,
	toEntityName: string | null
): void => {
	link1.hasNodeLeft = true;
	link1.hasNodeRight = false;
	link1.showBox = true;
	link1.sourceColor = entityColorMap.issuerEntityColor;
	link1.lineColor = entityColorMap.issuerEntityColor;
	link1.blockColor = entityColorMap.issuerEntityColor;
	link1.nodeLeftName = fromEntityName ?? '';
	link1.nodeLeftContextMenuItems = sourceBlocks;

	link2.hasNodeLeft = false;
	link2.hasNodeRight = true;
	link2.hasArrowRight = true;
	link2.showBox = false;
	link2.lineColor = entityColorMap.issuerEntityColor;
	link2.nodeRightContextMenuItems = destinationBlocks;
	link2.nodeRightName = toEntityName ?? '';
	link2.label = toEntityName ?? '';
};

const configureSingleNode = ({
	link1,
	link2,
	entityColorMap,
	blocks,
	isIssuerEntity,
	isMainEntity,
	entityName,
}: {
	link1: LinkCalculatedProps;
	link2: LinkCalculatedProps;
	entityColorMap: EntityColorMapInput;
	blocks: string[];
	isIssuerEntity: boolean;
	isMainEntity: boolean;
	entityName: string | null;
}): void => {
	if (isIssuerEntity) {
		link1.hasNodeLeft = true;
		link1.nodeLeftContextMenuItems = blocks;
		link1.nodeLeftName = entityName ?? '';
		link1.sourceColor = entityColorMap.issuerEntityColor;
		link1.lineColor = TRANSPARENT;

		link2.hidden = true;
	} else if (isMainEntity) {
		link1.hidden = true;

		link2.sourceColor = entityColorMap.mainEntityColor;
		link2.hasNodeLeft = true;
		link2.nodeLeftContextMenuItems = blocks;
		link2.nodeLeftName = entityName ?? '';
		link2.lineColor = TRANSPARENT;
	} else {
		link1.hidden = true;

		link2.hasNodeRight = true;
		link2.nodeRightContextMenuItems = blocks;
		link2.nodeRightName = entityName ?? '';
		link2.lineColor = TRANSPARENT;
		link2.destinationColor = entityColorMap.otherEntityColor;
	}
};
